// src/ine/ine_config/pages/IneBookingsPage.jsx
import React, { useEffect, useState } from "react";
import AutoDismissAlert from "../../../components/autoDismiss";
import { API_URLS } from "../../../api/apiConfig";
import { postWithAuth } from "../../../api/api";
import Form from "react-bootstrap/Form";

const IneBookingsPage = () => {
  // tabla
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  // estado de UI
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("info");
  const [showAlert, setShowAlert] = useState(false);

  // filtros
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [monthFrom, setMonthFrom] = useState(getPrevMonthISO());
  const [monthTo, setMonthTo] = useState(getPrevMonthISO());

  function openAlert(msg, type = "danger") {
    setMessage(msg);
    setAlertType(type);
    setShowAlert(true);
  }

  function getMonthBounds(monthStr) {
    if (!monthStr) return [undefined, undefined];
    const [y, m] = monthStr.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const s = `${first.getFullYear()}-${pad(first.getMonth() + 1)}-${pad(
      first.getDate()
    )}`;
    const e = `${last.getFullYear()}-${pad(last.getMonth() + 1)}-${pad(
      last.getDate()
    )}`;
    return [s, e];
  }

  async function fetchData() {
    setIsLoading(true);
    try {
      const [fromStart] = getMonthBounds(monthFrom);
      const [, toEnd] = getMonthBounds(monthTo);

      const payload = {
        page,
        limit,
        search_text: searchText || undefined,
        start_date: fromStart || undefined,
        end_date: toEnd || undefined,
      };

      const resp = await postWithAuth(API_URLS.LIST_INE_PEX_REPORTS, payload);
      if (resp?.message === "success") {
        const data = resp.data || {};
        setItems(data.reports || data.list || []);
        setTotal(data.total ?? 0);
      } else {
        openAlert(resp?.detail || "Error al obtener reservas INE");
      }
    } catch (e) {
      console.error(e);
      openAlert("Ha ocurrido un error conectando con el servidor");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      {showAlert && (
        <div className="mb-5">
          <AutoDismissAlert
            message={message}
            alertType={alertType}
            onClose={() => setShowAlert(false)}
          />
        </div>
      )}

      {/* Título simple */}
      <div className="mb-6">
        <h3 className="fw-bold mb-1"></h3>
      </div>

      {/* Filtros (grid consistente y centrado vertical) */}
      <div className="mb-6">
        <Form
          className="row row-cols-auto g-3 align-items-center"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            fetchData();
          }}
        >
          <Form.Group className="col-auto d-flex align-items-center">
            <Form.Label className="mb-0 me-2">Desde</Form.Label>
            <Form.Control
              type="month"
              value={monthFrom}
              onChange={(e) => setMonthFrom(e.target.value)}
              style={{ minWidth: 160 }}
            />
          </Form.Group>

          <Form.Group className="col-auto d-flex align-items-center">
            <Form.Label className="mb-0 me-2">Hasta</Form.Label>
            <Form.Control
              type="month"
              value={monthTo}
              onChange={(e) => setMonthTo(e.target.value)}
              style={{ minWidth: 160 }}
            />
          </Form.Group>

          <Form.Group className="col-auto d-flex align-items-center">
            {/* Si no quieres label visible aquí, puedes poner uno “visually hidden” para accesibilidad */}
            <Form.Label className="visually-hidden">Buscar</Form.Label>
            <Form.Control
              type="text"
              placeholder="Booking, habitación, estado…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ minWidth: 260 }}
            />
          </Form.Group>

          <div className="col-auto d-flex align-items-center">
            <button
              type="submit"
              className="btn btn-sm btn-primary"
              onClick={() => setPage(1)}
            >
              Aplicar filtros
            </button>
          </div>
        </Form>
      </div>

      {/* Tabla (sin card) */}
      <div className="table-responsive" style={{ overflowX: "auto" }}>
        <table className="table align-middle table-row-dashed fs-6 gy-5">
          <thead>
            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
              <th
                className=""
                tabIndex="0"
                aria-controls="kt_table_users"
                rowSpan="1"
                colSpan="1"
              >
                Booking ID
              </th>
              <th>Edificio</th>
              <th>Unidad</th>
              <th>Habitación</th>
              <th>País</th>
              <th>Estado</th>
              <th
                className=""
                tabIndex="0"
                aria-controls="kt_table_users"
                rowSpan="1"
                colSpan="1"
              >
                Inicio
              </th>
              <th
                className=""
                tabIndex="0"
                aria-controls="kt_table_users"
                rowSpan="1"
                colSpan="1"
              >
                Fin
              </th>
              <th>Cód. Postal</th>
              <th>Importe total</th>
              <th>Tipo</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 fw-semibold">
            {isLoading ? (
              <tr>
                <td colSpan="9" className="text-center py-5">
                  <div
                    className="spinner-border text-color-amarillo-livensa"
                    style={{ width: "3rem", height: "3rem" }}
                    role="status"
                  >
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                </td>
              </tr>
            ) : (items || []).length > 0 ? (
              items.map((row, idx) => (
                <tr key={row.id || row._id || idx}>
                  <td>{row.booking_id}</td>
                  <td>{row.building}</td>
                  <td>{row.unit_name}</td>
                  <td>{row.room_name}</td>
                  <td>{row.address_country}</td>
                  <td>{row.status}</td>
                  <td>{row.booking_start}</td>
                  <td>{row.booking_end}</td>
                  <td>{row.post_code}</td>
                  <td>{formatMoney(row.total_rent)}</td>
                  <td>{row.booking_type}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-5">
                  No se encontraron resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación + tamaño de página (sin card) */}
      <div className="row mt-4">
        <div className="col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end gap-3">
          <Form.Select
            style={{ width: 150 }}
            value={limit}
            onChange={(e) => {
              setPage(1);
              setLimit(Number(e.target.value));
            }}
          >
            <option value="5">5 por página</option>
            <option value="10">10 por página</option>
            <option value="20">20 por página</option>
            <option value="50">50 por página</option>
          </Form.Select>

          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </button>
              </li>
              <li className="page-item disabled">
                <span className="page-link">
                  Página {page} de {totalPages}
                </span>
              </li>
              <li
                className={`page-item ${page >= totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                >
                  Siguiente
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

export default IneBookingsPage;

// Helpers
function getPrevMonthISO() {
  const now = new Date();
  const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastPrev = new Date(firstThis - 1);
  const y = lastPrev.getFullYear();
  const m = String(lastPrev.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function formatMoney(val) {
  const n = Number(val);
  if (Number.isNaN(n)) return val ?? "";
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
}
